import {
  AriaTelemetryClient,
  AriaTo1DSContextUsed,
  CustomContext,
  initializeTelemetryClient,
  logger,
  LoggingContext,
  updateToCompliantAriaClient,
} from "@mwt/web-js-telemetry";
import { useEffect, useState } from "react";
import { createContainer, useContainer } from "unstated-next";
import {
  ApplicationInsights,
  IExtendedConfiguration,
} from "@microsoft/1ds-analytics-web-js";
import { DataContexts } from "@microsoft/1ds-privacy-guard-js";

import { CustomContextProperties } from "../types";
import {
  telemetryClientConfig,
  telemetryClientForReConfig,
} from "../telemetry-context/TelemetryProvider";

export const loggingContext: LoggingContext = {};
logger.initialize(loggingContext);

export const updateLoggingContext = (
  commonProperties?: Partial<AriaTo1DSContextUsed>,
  customProperties?: Partial<CustomContextProperties>
) => {
  // Nothing else to do when this param not provided
  /* istanbul ignore else */
  if (commonProperties) {
    loggingContext.commonProperties = {
      ...loggingContext.commonProperties,
      ...commonProperties,
    };
  }

  // Nothing else to do when this param not provided
  /* istanbul ignore else */
  if (customProperties) {
    loggingContext.customProperties = {
      ...loggingContext.customProperties,
      ...customProperties,
    } as CustomContext;
  }

  logger.updateContext(loggingContext);
};

const useTelemetryContextHook = () => {
  const [isCompliant, setIsCompliant] = useState(false);
  const [ariaTelemetryClient, setAriaTelemetryClient] = useState<
    AriaTelemetryClient | undefined
  >();
  const [ingestionKey, setIngestionKey] = useState<string | undefined>();
  const [tenantId, setTenantId] = useState<string | undefined>();
  const [logToConsole, setLogToConsole] = useState<boolean>(false);
  const privacyGuardUsername = "EHR Client";
  const privacyGuardUserAlias = "EHR";

  const dataCtx: DataContexts = {
    UserName: privacyGuardUsername,
    UserAlias: privacyGuardUserAlias,
    MachineIds: [],
    OutOfScopeIdentifiers: [],
  };

  const coreConfig: IExtendedConfiguration = {
    instrumentationKey: ingestionKey,
    extensionConfig: {
      context: dataCtx,
      notificationEventName: "EHREventInfoName.PrivacyConcern",
      summaryEventName: "ehr-client-privacy-guard-summary",
      useEventFieldPrefix: true,
    },
  };

  const appInsights: ApplicationInsights = new ApplicationInsights();
  appInsights.initialize(coreConfig, []);

  useEffect(() => {
    if (ingestionKey && !ariaTelemetryClient) {
      const ariaClient = new AriaTelemetryClient({
        ...telemetryClientConfig,
        instrumentationKey: ingestionKey,
      });
      initializeTelemetryClient(ariaClient, logToConsole);
      setAriaTelemetryClient(ariaClient);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingestionKey]);

  useEffect(() => {
    if (ariaTelemetryClient && tenantId && !isCompliant) {
      ariaTelemetryClient
        .getCompliantAriaClient(tenantId, {
          ...telemetryClientForReConfig,
          instrumentationKey: ingestionKey,
        })
        .then(
          (client: AriaTelemetryClient) => {
            updateToCompliantAriaClient(client);
            setAriaTelemetryClient(client);
            setIsCompliant(true);
          },
          (reason: string) => {
            // rejection
            if (logToConsole) {
              console.log(
                `There has been problems when trying to update to compliant aria client. Failure reason: ${reason}`
              );
            }
            setIsCompliant(false);
          }
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  return {
    ariaTelemetryClient,
    setTelementryIngestionKey: setIngestionKey,
    setTelemetryTenantId: setTenantId,
    setLogTelemetryToConsole: setLogToConsole,
  };
};

const TelemetryContext = createContainer(useTelemetryContextHook);

export const TelemetryContextProvider = TelemetryContext.Provider;

export const useTelemetryContext = () => useContainer(TelemetryContext);
